import { useState, useEffect, useRef } from "react";
import type { Editor, JSONContent } from "@tiptap/react";
import { z } from "zod";
import type { ParseError } from "@/parser/templateParser";
import type { TemplateMeta } from "@/parser/templateSerializer";
import type { SubTemplateData } from "@/types/evaluation";
import type { Evaluation } from "@/types/evaluation";
import type { EditorInitData } from "@/components/editor/useEditorInit";

function readStoredBoolean(key: string, defaultValue: boolean): boolean {
  const stored = localStorage.getItem(key);
  if (stored === null) {
    return defaultValue;
  }
  try {
    const result = z.boolean().safeParse(JSON.parse(stored));
    return result.success ? result.data : defaultValue;
  } catch {
    return defaultValue;
  }
}

function useFormState(
  mode: "build" | "fill",
  evaluation: Evaluation | undefined,
  initialData: EditorInitData,
) {
  const [meta, setMeta] = useState<TemplateMeta>(initialData.meta);
  const [name, setName] = useState(initialData.name);
  const [unifiedDocument, setUnifiedDocument] = useState<JSONContent | null>(
    initialData.initialDoc,
  );
  const [values, setValues] = useState<Record<string, unknown>>(
    mode === "fill" ? (evaluation?.values ?? {}) : {},
  );
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    mode === "fill" ? (evaluation?.fillVariables ?? {}) : {},
  );
  const [sectionToggles, setSectionToggles] = useState<Record<string, boolean>>(
    mode === "fill" ? (evaluation?.sectionToggles ?? {}) : {},
  );
  const [fieldToggles, setFieldToggles] = useState<Record<string, boolean>>(
    mode === "fill" ? (evaluation?.fieldToggles ?? {}) : {},
  );
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [subTemplateValues, setSubTemplateValues] = useState<Record<string, SubTemplateData>>(
    mode === "fill" ? (evaluation?.subTemplateValues ?? {}) : (initialData.subTemplateValues ?? {}),
  );
  return {
    collapsedSections,
    fieldToggles,
    meta,
    name,
    sectionToggles,
    setCollapsedSections,
    setFieldToggles,
    setMeta,
    setName,
    setSectionToggles,
    setSubTemplateValues,
    setUnifiedDocument,
    setValues,
    setVariableValues,
    subTemplateValues,
    unifiedDocument,
    values,
    variableValues,
  };
}

function useEditorRefs() {
  const editorRef = useRef<Editor | null>(null);
  const [editorWrapperEl, setEditorWrapperEl] = useState<HTMLDivElement | null>(null);
  const codeMirrorContainerRef = useRef<HTMLDivElement>(null);
  const reportPaneRef = useRef<HTMLDivElement>(null);
  const scrollInitiatorRef = useRef<"tiptap" | "cm" | "toc" | null>(null);
  const scrollInitiatorTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const tiptapEditingRef = useRef(false);
  const tiptapEditingTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  return {
    codeMirrorContainerRef,
    editorRef,
    editorWrapperEl,
    reportPaneRef,
    scrollInitiatorRef,
    scrollInitiatorTimerRef,
    setEditorWrapperEl,
    tiptapEditingRef,
    tiptapEditingTimerRef,
  };
}

export function useEditorState(
  mode: "build" | "fill",
  evaluation: Evaluation | undefined,
  initialData: EditorInitData,
) {
  const form = useFormState(mode, evaluation, initialData);
  const [parseErrors, setParseErrors] = useState<ParseError[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showInsertPicker, setShowInsertPicker] = useState(false);

  const storagePrefix = mode === "build" ? "builder" : "unified";
  const [showReport, setShowReport] = useState(() =>
    readStoredBoolean(`${storagePrefix}-show-report`, true),
  );
  const [showSource, setShowSource] = useState(() =>
    readStoredBoolean(`${storagePrefix}-show-source`, mode === "build"),
  );

  const refs = useEditorRefs();

  useEffect(() => {
    if (mode !== "fill" || !evaluation) {
      return;
    }
    form.setName(evaluation.name);
    form.setValues(evaluation.values);
    form.setVariableValues(evaluation.fillVariables);
    form.setSectionToggles(evaluation.sectionToggles);
    form.setSubTemplateValues(evaluation.subTemplateValues);
    form.setUnifiedDocument((evaluation.unifiedDocument as JSONContent | undefined) ?? null);
  }, [evaluation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    ...form,
    menuOpen,
    parseErrors,
    setMenuOpen,
    setParseErrors,
    setShowDeleteConfirm,
    setShowInsertPicker,
    setShowReport,
    setShowSource,
    showDeleteConfirm,
    showInsertPicker,
    showReport,
    showSource,
    storagePrefix,
    ...refs,
  };
}
